// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// render.ts — the board, as DOM.
//
// DOM rather than canvas, because brood is about forty elements at its widest and every one of
// them wants a crisp text glyph, an aria-label and a CSS transition. What canvas would buy — many
// entities, particles, continuous motion — this game does not have.
//
// EVERY POSITION COMES FROM layout.ts ARITHMETIC. Nothing here reads an element's
// getBoundingClientRect to decide where anything goes: in an automated browser pane a
// transform-positioned element reports a stale rect at the origin while the screenshot is
// perfectly correct, so a layout that measures itself is a layout that cannot be verified. The one
// measurement is the stage box, and `resize` refuses to compute a frame from a zero-sized one —
// a backgrounded tab measures 0, and a board sized from 0 is a blank page.

import { SQRT3 } from './hex';
import { KINDS, tileKind, tileSeat } from './kinds';
import {
  computeFrame,
  corners,
  hexAt,
  minSeparation,
  onFrame,
  screenOf,
  type Frame,
  R_ACT_MIN,
  R_READ_MIN,
  TARGET_MIN,
} from './layout';
import { PALETTE, seatColour, seatHasInnerRing, seatName } from './palette';
import type { Position } from './rules';

export interface Marks {
  /** Empty cells a new tile could be set down in. */
  readonly place: readonly number[];
  /** Cells the selected piece may move to. */
  readonly dest: readonly number[];
  /** Cells a Tender could lift a tile out of, or set one down in. */
  readonly lift: readonly number[];
  /** The piece currently picked up. */
  readonly selected: number | null;
  /** The candidate being aimed at while a crowded set waits on a confirm. */
  readonly aimed: number | null;
  /** Cells that cannot move at all, drawn with a hatch so the hardest rule is always visible. */
  readonly pinned: readonly number[];
  /** The cell a tile arrived in on the last ply, for the landing flourish. */
  readonly landed: number | null;
  /** Which seat is looking. */
  readonly self: number;
}

export interface View {
  resize(): boolean;
  frame(): Frame;
  /** Rebuild the board for a position. `focus` decides the frame; pass the action set while a
   *  piece is selected and null the rest of the time. */
  paint(p: Position, marks: Marks, focus: readonly number[] | null): void;
  /** Stage-space coordinates of a client point. */
  toStage(clientX: number, clientY: number): { x: number; y: number };
  /** The hex under a client point. */
  hexAtClient(clientX: number, clientY: number): number;
  shake(): void;
  destroy(): void;
}

const HEX_PATH = (R: number): string =>
  corners(R)
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${(x + R).toFixed(2)},${(y + (SQRT3 * R) / 2).toFixed(2)}`)
    .join('') + 'Z';

export function createView(host: HTMLElement): View {
  host.classList.add('board');
  host.innerHTML = '';
  let frame: Frame = computeFrame([], 1, 1);
  let box = { w: 0, h: 0 };

  const el = (cls: string): HTMLElement => {
    const d = document.createElement('div');
    d.className = cls;
    return d;
  };

  const marksLayer = el('layer marks');
  const tilesLayer = el('layer tiles');
  host.append(marksLayer, tilesLayer);

  function resize(): boolean {
    // Measure the PARENT. Measuring the host feeds its own size back in and shrinks the board a
    // little on every call.
    const parent = host.parentElement ?? host;
    const w = parent.clientWidth;
    const h = parent.clientHeight;
    if (w < 2 || h < 2) return false;
    box = { w, h };
    return true;
  }

  /**
   * The board frame is what you read; the action frame is what you act in. But switching to the
   * action frame RESCALES AND RECENTRES EVERYTHING, and a board that jumps the instant you touch a
   * tile puts every destination somewhere new just as you were reaching for it. So the action
   * frame is a fallback, not a mode: if the board frame already shows every candidate with enough
   * room between them to tap one, it is kept, and the board simply does not move.
   */
  function frameFor(focus: readonly number[] | null, occupied: readonly number[]): Frame {
    const board = computeFrame(occupied, box.w, box.h, R_READ_MIN);
    if (!focus || focus.length === 0) return board;
    const roomy = minSeparation(board, focus) >= TARGET_MIN;
    if (roomy && focus.every((k) => onFrame(board, k))) return board;
    return computeFrame(focus, box.w, box.h, R_ACT_MIN);
  }

  function place(node: HTMLElement, k: number, R: number): void {
    const { x, y } = screenOf(frame, k);
    node.style.width = `${2 * R}px`;
    node.style.height = `${SQRT3 * R}px`;
    node.style.transform = `translate(${(x - R).toFixed(2)}px, ${(y - (SQRT3 * R) / 2).toFixed(2)}px)`;
  }

  function marker(k: number, kind: 'place' | 'dest' | 'lift' | 'aim', R: number): HTMLElement {
    const m = el(`mark mark-${kind}`);
    m.style.setProperty('--edge', kind === 'aim' ? PALETTE.aim : PALETTE[kind]);
    place(m, k, R);
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', `0 0 ${2 * R} ${SQRT3 * R}`);
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', HEX_PATH(R));
    path.setAttribute('class', 'markshape');
    svg.appendChild(path);
    m.appendChild(svg);
    return m;
  }

  function paint(p: Position, marks: Marks, focus: readonly number[] | null): void {
    const occupied = p.occupiedCells();
    frame = frameFor(focus, [...occupied, ...marks.place]);
    const R = frame.R;
    host.style.setProperty('--r', `${R}px`);
    host.classList.toggle('tiny', R < R_ACT_MIN);

    // Markers first, so a tile always paints on top of the cell it stands in.
    const mf = document.createDocumentFragment();
    for (const k of marks.place) mf.appendChild(marker(k, 'place', R));
    for (const k of marks.dest) mf.appendChild(marker(k, 'dest', R));
    for (const k of marks.lift) mf.appendChild(marker(k, 'lift', R));
    if (marks.aimed !== null) mf.appendChild(marker(marks.aimed, 'aim', R));
    marksLayer.replaceChildren(mf);

    const tf = document.createDocumentFragment();
    const pinned = new Set(marks.pinned);
    for (const k of occupied) {
      const stack = p.stack(k);
      const t = stack[stack.length - 1];
      const kind = tileKind(t);
      const seat = tileSeat(t);
      const node = el('tile');
      node.dataset.cell = String(k);
      if (seat === marks.self) node.classList.add('mine');
      if (kind === 0) node.classList.add('matriarch');
      if (stack.length > 1) node.classList.add('stacked');
      if (pinned.has(k)) node.classList.add('pinned');
      if (marks.selected === k) node.classList.add('selected');
      if (marks.landed === k) node.classList.add('landed');
      node.style.setProperty('--fill', seatColour(seat));
      place(node, k, R);

      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', `0 0 ${2 * R} ${SQRT3 * R}`);
      if (stack.length > 1) {
        // The buried tile peeks out from underneath, offset and darkened, so a stack reads as two
        // sheets of paper rather than as one tile that changed colour.
        const under = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        under.setAttribute('d', HEX_PATH(R));
        under.setAttribute('class', 'under');
        under.setAttribute('fill', seatColour(tileSeat(stack[stack.length - 2])));
        under.setAttribute('transform', `translate(${(R * 0.13).toFixed(2)},${(R * 0.13).toFixed(2)})`);
        svg.appendChild(under);
      }
      const face = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      face.setAttribute('d', HEX_PATH(R));
      face.setAttribute('class', 'face');
      svg.appendChild(face);
      // The redundant SHAPE cue for side. The two brood colours separate cleanly for the common
      // colour vision deficiencies but are almost identical in luminance, so a greyscale view
      // could not tell the armies apart on colour alone. Every Sky tile carries a dashed inner
      // ring; no Amber tile does.
      if (seatHasInnerRing(seat)) {
        const ring = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        ring.setAttribute('d', HEX_PATH(R * 0.66));
        ring.setAttribute('class', 'inner');
        ring.setAttribute('transform', `translate(${(R * 0.34).toFixed(2)},${((SQRT3 * R * 0.34) / 2).toFixed(2)})`);
        svg.appendChild(ring);
      }
      node.appendChild(svg);

      const g = el('glyph');
      g.textContent = KINDS[kind].glyph;
      node.appendChild(g);
      if (stack.length > 1) {
        const badge = el('badge');
        badge.textContent = String(stack.length);
        badge.style.setProperty('--under', seatColour(tileSeat(stack[stack.length - 2])));
        node.appendChild(badge);
      }
      node.setAttribute(
        'aria-label',
        `${KINDS[kind].name}, ${seatName(seat)}${stack.length > 1 ? `, on top of a ${KINDS[tileKind(stack[stack.length - 2])].name}` : ''}${pinned.has(k) ? ', cannot move' : ''}`,
      );
      tf.appendChild(node);
    }
    tilesLayer.replaceChildren(tf);
  }

  return {
    resize,
    frame: () => frame,
    paint,
    toStage(clientX, clientY) {
      const r = host.getBoundingClientRect();
      return { x: clientX - r.left, y: clientY - r.top };
    },
    hexAtClient(clientX, clientY) {
      const r = host.getBoundingClientRect();
      return hexAt(frame, clientX - r.left, clientY - r.top);
    },
    shake() {
      host.classList.remove('shake');
      void host.offsetWidth;
      host.classList.add('shake');
    },
    destroy() {
      host.innerHTML = '';
      host.classList.remove('board');
    },
  };
}

/** The six-segment ring that shows how boxed in a Matriarch is. Its whole reason for existing is
 *  that the win condition stays trackable even when the tile itself is a long way up the board. */
export function meterHtml(closed: number, need: number, seat: number): string {
  const parts: string[] = [];
  for (let i = 0; i < 6; i++) {
    const on = i < closed;
    parts.push(
      `<span class="seg${on ? ' on' : ''}" style="${on ? `background:${seatColour(seat)}` : ''}"></span>`,
    );
  }
  const danger = closed >= need - 1 && closed < need;
  return `<span class="meter${danger ? ' danger' : ''}" role="img" aria-label="${closed} of ${need} sides closed">${parts.join('')}<b>${closed}/${need}</b></span>`;
}
